import Skeleton from '../Skeleton';
import { homeStackProps, adminHomeCompactPadding } from '../../dashboard/home/homeTokens';
import { rd } from './skeletonParts';
import {
  skeletonHomeLayout, SkelCard, Bar, SkelFigure, SkelRows, SkelGroupLabel, SkelLegend, SkelTodayBody, SkelPipelineBody,
} from './homeSkeletonParts';
import { SkelMonthBand, SkelWeekBand } from './homeBandSkeletons';
import { pairSpanMap } from '../../dashboard/home/pairSpans';

/**
 * Admin + Billing dashboard skeletons (KAN-946). They mirror the rebuilt home dashboards
 * (AdminHomeCards / BillingHomeCards): the same stack (`homeStackProps` -> the 12-column
 * grid on web, one column under the dock), the same card order and `data-span`s, and the
 * real HomeCard shell + `cvx-home-*` layout classes filled with skeleton bars (parts in
 * homeSkeletonParts.js / homeBandSkeletons.js). Keep the ORDER below in step with those two
 * files (CLAUDE.md skeleton parity rule). The AI briefing card has no placeholder (it
 * appears only when AI is on, and never had one).
 *
 * Spans come from `pairSpans` (KAN-1003), the same engine as the live homes; the skeleton assumes every
 * card renders. Web and tablet place them (12 / 2 columns), the phone is one column.
 *
 *   admin      web      Today | Pipeline · Estimates | Activity · Month band · Week band
 *              tablet   same as web (paired in the 2-column grid)
 *              phone    Pipeline · Today · Estimates · Month · Week · Activity (one column)
 *   billing    all      Pipeline | Overdue by age · Month band · Recent invoices | Recent estimates
 *   operations all      Today | Needs a look · Unassigned | Customer pipeline · Week band
 *   (KAN-971)
 *              (no year filter row: no Operations card reads one, so Today is the first card)
 */

/** Year/month filter trigger (DashboardPeriodFilter): ONE left-aligned, content-sized bar —
 *  44px on every touch width (phone AND tablet), 38px on web, like the real trigger. */
function PeriodFilterSkeleton({ touch }) {
  return (
    <div aria-hidden="true">
      <Skeleton width={170} height={touch ? 44 : 38} borderRadius={rd.md} />
    </div>
  );
}

/**
 * The skeleton twin of DashboardTopChrome's stack: the same grid / column, same gap. `padded` = the
 * admin phone/tablet body, which adds its top padding only (`adminHomeCompactPadding`, the SAME
 * constant AdminDashboardMobile reads; the frame owns the side gutter, KAN-1005/1006); the billing
 * body is padded by its tab-content wrapper, so it is not.
 * `periodFilter` = the year/month trigger row on top: admin and billing have one, Operations does not.
 */
function HomeSkeletonStack({ layout, testId, padded = false, periodFilter = true, children }) {
  const { style, ...rest } = homeStackProps(layout);
  return (
    <div
      {...rest}
      style={{
        ...style, width: '100%', minWidth: 0, boxSizing: 'border-box',
        ...(padded && layout !== 'web' ? { padding: adminHomeCompactPadding(layout === 'tablet') } : null),
      }}
      data-testid={testId}
      data-skeleton-layout={layout}
    >
      {periodFilter ? <PeriodFilterSkeleton touch={layout !== 'web'} /> : null}
      {children}
    </div>
  );
}

// The Today card (TodayCard, RD-DASH-009): the hero line + stop bar, then the compact crew rows. A phone
// held upright, tablet and half-width web have one column of rows; a full-width card and a phone held
// sideways have two — all from homeToday.css, so the skeleton changes shape with the real card.
export function TodaySkeleton({ layout, span }) {
  return (
    <SkelCard layout={layout} span={span} titleWidth={64} action={48}>
      <SkelTodayBody layout={layout} />
    </SkelCard>
  );
}

function PipelineSkeleton({ variant, span }) {
  const layout = variant === 'narrow' ? 'web' : variant;
  return (
    <SkelCard layout={layout} span={span} className="cvx-home-pipeline" titleWidth={118}>
      <SkelPipelineBody variant={variant} />
    </SkelCard>
  );
}

function EstimatesWaitingSkeleton({ layout, span }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={span} stretch={web} titleWidth={124} action={64}>
      <SkelFigure layout={layout} />
      <SkelRows n={web ? 5 : 3} />
    </SkelCard>
  );
}

function ActivitySkeleton({ layout, span }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={span} stretch={web} titleWidth={110} action={56}>
      <div className="cvx-home-groups">
        <SkelGroupLabel />
        <SkelRows n={3} square />
        <SkelGroupLabel />
        <SkelRows n={web ? 3 : 2} square />
      </div>
    </SkelCard>
  );
}

function OverdueByAgeSkeleton({ layout, span }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={span} stretch={web} titleWidth={112} action={64}>
      <Skeleton width="100%" height={10} borderRadius={999} />
      <SkelLegend rows={3} lines sub />
    </SkelCard>
  );
}

function RecentListSkeleton({ layout, side, titleWidth, action, span }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={span} stretch={web} titleWidth={titleWidth} action={action} testId={`recent-${side}-skeleton`}>
      <SkelRows n={4} />
    </SkelCard>
  );
}

const halves = (keys, strips, layout) => {
  const map = pairSpanMap(keys.map((key) => ({ key, kind: strips.includes(key) ? 'strip' : 'half' })), layout);
  // The phone column needs no spans: its skeleton cards carry none.
  return layout === 'phone' ? Object.fromEntries(keys.map((key) => [key, undefined])) : map;
};

export function AdminDashboardSkeleton({ isMobile = false, isTablet = false }) {
  const layout = skeletonHomeLayout(isMobile, isTablet);
  const web = layout === 'web';
  const testId = web ? 'admin-dashboard-skeleton' : 'admin-dashboard-mobile-skeleton';
  // Web AND tablet share the paired order (AdminHomeCards); only the one-column phone puts money first.
  if (layout !== 'phone') {
    const sp = halves(['today', 'pipeline', 'estimates', 'activity', 'month', 'week'], ['month', 'week'], layout);
    return (
      <HomeSkeletonStack layout={layout} testId={testId} padded={!web}>
        <TodaySkeleton layout={layout} span={sp.today} />
        <PipelineSkeleton variant={web ? 'narrow' : layout} span={sp.pipeline} />
        <EstimatesWaitingSkeleton layout={layout} span={sp.estimates} />
        <ActivitySkeleton layout={layout} span={sp.activity} />
        <SkelMonthBand variant="admin" layout={layout} />
        <SkelWeekBand layout={layout} />
      </HomeSkeletonStack>
    );
  }
  const sp = halves(['pipeline', 'today', 'estimates', 'month', 'week', 'activity'], ['month', 'week'], layout);
  return (
    <HomeSkeletonStack layout={layout} testId={testId} padded>
      <PipelineSkeleton variant={layout} span={sp.pipeline} />
      <TodaySkeleton layout={layout} span={sp.today} />
      <EstimatesWaitingSkeleton layout={layout} span={sp.estimates} />
      <SkelMonthBand variant="admin" layout={layout} />
      <SkelWeekBand layout={layout} />
      <ActivitySkeleton layout={layout} span={sp.activity} />
    </HomeSkeletonStack>
  );
}

export function BillingDashboardSkeleton({ isMobile = false, isTablet = false }) {
  const layout = skeletonHomeLayout(isMobile, isTablet);
  const web = layout === 'web';
  const sp = halves(['pipeline', 'overdue', 'month', 'invoices', 'estimates'], ['month'], layout);
  return (
    <HomeSkeletonStack layout={layout} testId={web ? 'billing-dashboard-skeleton' : 'billing-dashboard-mobile-skeleton'}>
      <PipelineSkeleton variant={web ? 'narrow' : layout} span={sp.pipeline} />
      <OverdueByAgeSkeleton layout={layout} span={sp.overdue} />
      <SkelMonthBand variant="billing" layout={layout} />
      <RecentListSkeleton layout={layout} side="invoices" titleWidth={118} action={96} span={sp.invoices} />
      <RecentListSkeleton layout={layout} side="estimates" titleWidth={132} action={92} span={sp.estimates} />
    </HomeSkeletonStack>
  );
}

// ── Operations home skeleton (KAN-971 phase B) ───────────────────────────────
// Mirrors OpsHomeCards: the same stack, card order and spans. The Today and Week shapes are the
// admin ones above; the three list cards borrow the real HomeCard shell too.

function UnassignedSkeleton({ layout, span }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={span} stretch={layout !== 'phone'} titleWidth={140} action={48}>
      <SkelFigure layout={layout} inline />
      <SkelRows n={web ? 5 : 3} trailing={false} />
      <div className="cvx-home-foot" aria-hidden="true"><Bar width="78%" height={11} line={18} /></div>
    </SkelCard>
  );
}

function NeedsLookSkeleton({ layout, span }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={span} stretch={layout !== 'phone'} titleWidth={104} action={60}>
      <SkelRows n={web ? 4 : 3} square trailing={false} />
    </SkelCard>
  );
}

function CustomerPipelineSkeleton({ layout, span }) {
  return (
    <SkelCard layout={layout} span={span} stretch={layout !== 'phone'} titleWidth={118} action={60}>
      <SkelFigure layout={layout} inline />
      <Skeleton width="100%" height={8} borderRadius={999} />
      <SkelLegend rows={4} lines />
    </SkelCard>
  );
}

export function OperationsDashboardSkeleton({ isMobile = false, isTablet = false }) {
  // One order on every layout (KAN-1003); tablet pairs them in its 2-column grid.
  const layout = skeletonHomeLayout(isMobile, isTablet);
  const sp = halves(['today', 'needs', 'unassigned', 'pipeline', 'week'], ['week'], layout);
  return (
    <HomeSkeletonStack
      layout={layout}
      testId={layout === 'web' ? 'operations-dashboard-skeleton' : 'operations-dashboard-mobile-skeleton'}
      padded
      periodFilter={false}
    >
      <TodaySkeleton layout={layout} span={sp.today} />
      <NeedsLookSkeleton layout={layout} span={sp.needs} />
      <UnassignedSkeleton layout={layout} span={sp.unassigned} />
      <CustomerPipelineSkeleton layout={layout} span={sp.pipeline} />
      <SkelWeekBand layout={layout} />
    </HomeSkeletonStack>
  );
}
